import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ArrowDownTrayIcon } from '@gto/icons-react/outline/arrow-down-tray'
import { DocumentIcon } from '@gto/icons-react/outline/document'
import { XMarkIcon } from '@gto/icons-react/outline/x-mark'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Dialog } from '@gto/ui/dialog'
import { IconButton } from '@gto/ui/icon-button'
import { Typography } from '@gto/ui/typography'

import { CATEGORY_ICON, isImage, isVideo } from '@/components/asset-media'
import { ExtensionBadge } from '@/components/extension-badge'
import { formatFileSize } from '@/utils/format-file-size'

type AssetPreviewDialogProps = {
  open: boolean
  onClose: () => void
  name: string
  category: string
  contentType: string
  fileSize: number
  downloadUrl: string | null
}

const isPdf = (ct: string) => ct === 'application/pdf'

export const AssetPreviewDialog = ({
  open,
  onClose,
  name,
  category,
  contentType,
  fileSize,
  downloadUrl,
}: AssetPreviewDialogProps) => {
  const { formatMessage } = useIntl()
  const [broken, setBroken] = useState(false)
  const Icon = CATEGORY_ICON[category] ?? DocumentIcon
  const extension = contentType.split('/').pop()?.toUpperCase() ?? ''

  // `previewable` (not `previewable && !broken`) drives the shell width and
  // stage height so a failed image swaps to the fallback content without a
  // jarring dialog resize.
  const previewable =
    downloadUrl != null && (isImage(contentType) || isVideo(contentType) || isPdf(contentType))
  const showMedia = previewable && !broken

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup
          className={cn(
            'flex max-h-[90vh] max-w-[90vw] flex-col overflow-hidden rounded-lg border-none bg-paper-elevation0 p-0 sm:max-h-[90vh] sm:max-w-[90vw] sm:p-0',
            // 460 = the UploadDialog width — one shared number, not a third
            // arbitrary one.
            previewable ? 'w-[min(960px,90vw)]' : 'w-[460px]',
          )}
        >
          {/* One 48px row, everything in flow: the title takes all slack and
          truncates, so it can never collide with the actions. */}
          <div className="flex h-12 shrink-0 items-center gap-3 border-b border-divider py-0 pl-4 pr-2">
            <Dialog.Title
              className="min-w-0 flex-1 truncate"
              title={name}
            >
              {name}
            </Dialog.Title>

            <Typography
              className="whitespace-nowrap"
              color="text.secondary"
              variant="caption"
            >
              {`${extension} · ${formatFileSize(fileSize)}`}
            </Typography>

            {downloadUrl != null && (
              <Button
                data-dialog-cta
                size="small"
                variant="contained"
                startIcon={
                  <ArrowDownTrayIcon
                    height={14}
                    width={14}
                  />
                }
                onClick={() => window.open(downloadUrl, '_blank')}
              >
                <FormattedMessage id="assets.download" />
              </Button>
            )}

            <IconButton
              aria-label={formatMessage({ id: 'common.close' })}
              size="small"
              onClick={onClose}
            >
              <XMarkIcon
                height={18}
                width={18}
              />
            </IconButton>
          </div>

          {/* Fixed-height letterbox stage: media object-contains, nothing ever
          crops or scrolls. (PDF is the one exception — the iframe's reader
          scrolls its own document, which is expected behavior.) */}
          <div
            className={cn(
              'flex shrink-0 items-center justify-center overflow-hidden bg-paper-elevation1',
              previewable ? 'h-[min(640px,calc(90vh-48px))]' : 'h-[200px]',
            )}
          >
            {showMedia && isImage(contentType) && (
              <img
                alt={name}
                className="max-h-full max-w-full object-contain"
                src={downloadUrl}
                onError={() => setBroken(true)}
              />
            )}

            {showMedia && isVideo(contentType) && (
              // muted: autoplay with sound is hostile; the user unmutes via the
              // controls.
              <video
                autoPlay
                controls
                muted
                playsInline
                className="max-h-full max-w-full"
                src={downloadUrl}
              />
            )}

            {showMedia && isPdf(contentType) && (
              <iframe
                className="size-full"
                src={downloadUrl}
                title={name}
              />
            )}

            {!showMedia && (
              <div className="flex flex-col items-center gap-2">
                <Icon className="size-8 text-text-secondary" />
                <ExtensionBadge>{extension}</ExtensionBadge>
                <Typography
                  color="text.secondary"
                  variant="body2"
                >
                  <FormattedMessage id="assets.preview.unavailable" />
                </Typography>
              </div>
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
